"use client";

import { useMemo } from "react";
import { Check } from "@/components/icons";
import { getConditionsForType } from "@/lib/generated/product-types";
import { TradeListingForm } from "./trade-listing-cards";
import { Card } from "@/components/ui/card";

const RECYCLE_BENEFITS = [
  "Request pickup in minutes",
  "A rep replies within one business day",
  "We handle the freight",
];

const SELL_STEPS = [
  "Tell us what you have, how many, where it is and its condition",
  "We match your inventory with real buyer demand",
  "Accept a quote, we coordinate freight, you get paid, and you track the order in your account",
];

const RECYCLE_STEPS = [
  "Tell us what you have, how many and where it is",
  "A rep confirms whether pickup is free, or what we pay, and books a time",
  "We send the truck and collect it",
];

interface TradeListingHeroProps {
  intent: "sell" | "recycle";
  title: string;
  productSlug?: string;
  heroDescription?: string;
}

function CheckList({ items }: { items: readonly string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li
          key={item}
          className="flex items-center gap-2.5 text-sm text-foreground"
        >
          <Check className="size-4 shrink-0 text-success-strong" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function StepsList({ steps }: { steps: readonly string[] }) {
  return (
    <div>
      <p className="mb-3 text-overline uppercase text-muted-foreground">
        How it works
      </p>
      <ol className="space-y-3">
        {steps.map((step, index) => (
          <li key={step} className="flex items-start gap-3 text-sm">
            <span className="flex size-6 shrink-0 items-center justify-center border border-border bg-background text-xs font-semibold text-foreground">
              {index + 1}
            </span>
            <span className="pt-0.5 text-foreground">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function HeroPitch({
  intent,
  title,
  productSlug,
  heroDescription,
}: TradeListingHeroProps) {
  const conditions = useMemo(
    () => (productSlug ? getConditionsForType(productSlug) : []),
    [productSlug],
  );

  return (
    <div className="flex h-full flex-col gap-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          {title}
        </h1>
        {heroDescription && (
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {heroDescription}
          </p>
        )}
      </div>
      {(intent === "recycle" || conditions.length > 0) && (
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          {intent === "recycle" && <CheckList items={RECYCLE_BENEFITS} />}
          {conditions.length > 0 && (
            <div>
              <p className="mb-2.5 text-overline uppercase text-muted-foreground">
                {intent === "sell"
                  ? "Conditions we buy"
                  : "Conditions we accept"}
              </p>
              <CheckList
                items={conditions.map((condition) => condition.label)}
              />
            </div>
          )}
        </div>
      )}
      <StepsList steps={intent === "sell" ? SELL_STEPS : RECYCLE_STEPS} />
    </div>
  );
}

export function TradeListingHero(props: TradeListingHeroProps) {
  return (
    <section
      id="get-started"
      className="scroll-mt-24 border-y border-border bg-muted/40"
    >
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 items-start">
          <HeroPitch {...props} />
          <Card className="sm:p-8" padding="roomy">
            <TradeListingForm
              intent={props.intent}
              productSlug={props.productSlug}
            />
          </Card>
        </div>
      </div>
    </section>
  );
}
